import { DatePicker, Select } from '@arco-design/web-react'
import { IconCalendar } from '@arco-design/web-react/icon'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  type DashboardFilterState,
  type DashboardQuickPreset,
  formatDateTimeLocalSeconds,
  parseDateTimeLocalToSeconds,
} from '@/utils/timeRange'
import { useDashboardTokens } from '@/hooks/useDashboardData'
import { TOKEN_STATUS } from '@/utils/constants'
import type { ApiKey } from '@/types'

const PRESET_KEYS: { key: DashboardQuickPreset; labelKey: string }[] = [
  { key: 'today', labelKey: 'tt.time.today' },
  { key: 'yesterday', labelKey: 'tt.time.yesterday' },
  { key: 'last7', labelKey: 'tt.time.last7' },
  { key: 'last30', labelKey: 'tt.time.last30' },
  { key: 'thisMonth', labelKey: 'tt.time.thisMonth' },
  { key: 'custom', labelKey: 'tt.time.custom' },
]

function presetLabel(t: TFunction, preset: DashboardQuickPreset): string {
  const item = PRESET_KEYS.find((p) => p.key === preset)
  return item ? t(item.labelKey) : t('tt.time.custom')
}

interface DashboardFilterBarProps {
  value: DashboardFilterState
  onSelectPreset: (p: DashboardQuickPreset) => void
  onRangeChange: (start: number, end: number) => void
  onTokenChange: (tokenId: number | null) => void
}

const datePickerClass =
  'w-[200px] shrink-0 [&_.arco-picker-size-default]:h-9 [&_.arco-input-prefix_>_svg]:shrink-0 [&_.arco-picker-input>input]:text-[13px] [&_.arco-picker-input>input]:tracking-tight'

function tokenOptionClass(status: number): string | undefined {
  if (status === TOKEN_STATUS.ENABLED) return undefined
  return 'text-[var(--console-text-subtle)] opacity-70'
}

/**
 * 仪表盘行内时间筛选：左侧快捷按钮 + 右侧 API Key + 起止时间
 */
export function DashboardFilterBar({
  value,
  onSelectPreset,
  onRangeChange,
  onTokenChange,
}: DashboardFilterBarProps) {
  const { t } = useTranslation()
  const { start, end, activePreset, tokenId } = value
  const { data: tokens = [], isLoading: tokensLoading } = useDashboardTokens()

  const handleStartChange = (s: string | undefined) => {
    if (s == null || s === '') return
    const ts = parseDateTimeLocalToSeconds(s)
    if (ts == null) return
    onRangeChange(ts, Math.max(ts, end))
  }

  const handleEndChange = (s: string | undefined) => {
    if (s == null || s === '') return
    const ts = parseDateTimeLocalToSeconds(s)
    if (ts == null) return
    onRangeChange(Math.min(start, ts), ts)
  }

  const tokenOptions = [
    { label: t('tt.dashboard.allApiKeys'), value: 'all' as const },
    ...tokens.map((tok: ApiKey) => ({
      label: tok.name || `#${tok.id}`,
      value: String(tok.id),
      className: tokenOptionClass(tok.status),
    })),
  ]

  return (
    <div className="tt-card p-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        {PRESET_KEYS.map((item) => {
          const active = activePreset === item.key
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onSelectPreset(item.key)}
              className={
                active
                  ? 'px-3.5 py-1.5 text-xs font-medium rounded-lg border border-[color-mix(in_srgb,var(--brand-primary)_25%,transparent)] bg-[var(--brand-primary-light)] text-[var(--brand-primary)]'
                  : 'px-3.5 py-1.5 text-xs font-medium rounded-lg border border-transparent text-[var(--console-text-muted)] hover:bg-[var(--console-hover-item)]'
              }
            >
              {t(item.labelKey)}
            </button>
          )
        })}
      </div>

      <div className="flex flex-nowrap items-center gap-2 min-w-0 justify-end overflow-x-auto">
        <Select
          className="w-[168px] shrink-0"
          placeholder={t('tt.dashboard.filterByApiKey')}
          loading={tokensLoading}
          value={tokenId == null ? 'all' : String(tokenId)}
          onChange={(v) => {
            if (v === 'all' || v == null) {
              onTokenChange(null)
              return
            }
            const id = Number(v)
            onTokenChange(Number.isFinite(id) && id > 0 ? id : null)
          }}
          options={tokenOptions}
          triggerProps={{ autoAlignPopupWidth: false }}
        />
        <DatePicker
          showTime
          format="YYYY-MM-DD HH:mm:ss"
          className={datePickerClass}
          prefix={<IconCalendar className="text-[var(--console-text-subtle)] shrink-0" />}
          value={formatDateTimeLocalSeconds(start)}
          onChange={handleStartChange}
        />
        <span className="text-xs text-[var(--console-text-subtle)] shrink-0">{t('tt.time.to')}</span>
        <DatePicker
          showTime
          format="YYYY-MM-DD HH:mm:ss"
          className={datePickerClass}
          prefix={<IconCalendar className="text-[var(--console-text-subtle)] shrink-0" />}
          value={formatDateTimeLocalSeconds(end)}
          onChange={handleEndChange}
        />
      </div>
    </div>
  )
}

export function formatDashboardFilterSubtitle(f: DashboardFilterState, t: TFunction): string {
  return t('tt.dashboard.filterSubtitle', {
    preset: presetLabel(t, f.activePreset),
    start: formatDateTimeLocalSeconds(f.start),
    end: formatDateTimeLocalSeconds(f.end),
  })
}
